import { z } from "zod";
import { StandardResponseSchema, pageSchema } from "@/helpers/types";

export const CUSTOM_TAG_PREFIX = "custom_tag_";

export const getCustomTagSearchKey = (tag: string) => `${CUSTOM_TAG_PREFIX}${tag}`;

export const formatCustomTagLabel = (tag: string) =>
  tag.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

/**
 * Flatten custom_tag_filters from `{ tag: values[] }` into `custom_tag_{tag}: values`
 * query params, removing the nested object from the params.
 */
export const flattenCustomTagFilters = <
  T extends { custom_tag_filters?: Record<string, string[]> },
>(
  params: T
): Record<string, unknown> => {
  const { custom_tag_filters, ...rest } = params;
  const flatParams: Record<string, unknown> = { ...rest };
  if (custom_tag_filters) {
    for (const [tag, values] of Object.entries(custom_tag_filters)) {
      if (values.length > 0) {
        flatParams[getCustomTagSearchKey(tag)] = values;
      }
    }
  }
  return flatParams;
};

export const QueryGroupSchema = z.object({
  annualized_cost: z.coerce.number(),
  avg_cost: z.coerce.number(),
  avg_exec_time: z.string(),
  distinct_users: z.number(),
  distinct_warehouses: z.number(),
  full_query: z.string().nullable(),
  query_hash: z.string(),
  tags: z.array(z.string()),
  total_cost: z.coerce.number(),
  total_run: z.coerce.string(),
  trimmed_query: z.string(),
  users: z.array(z.string()).optional(),
  warehouses: z.array(z.string()).optional(),
  instance_id: z.coerce.string().optional(),
});

export interface QueryGroup extends z.infer<typeof QueryGroupSchema> {}

export const QueryTextResponseSchema = z.object({
  query_text: z.string(),
});

export interface QueryTextResponse extends z.infer<typeof QueryTextResponseSchema> {}

export const QueryTextIndicesResponseSchema = z.object({
  query_text: z.string(),
  spans: z.record(z.any()),
});

export interface QueryTextIndicesResponse extends z.infer<typeof QueryTextIndicesResponseSchema> {}

export const QueryTagsSchema = z.object({
  name: z.string(),
  description: z.string(),
  cost_savings: z.boolean(),
  time_savings: z.boolean(),
});

export interface QueryTags extends z.infer<typeof QueryTagsSchema> {}

export const QueryFiltersSchema = z.object({
  tags: z.array(QueryTagsSchema),
  users: z.array(z.string()),
  warehouses: z.array(z.string()),
  roles: z.array(z.string()),
  query_types: z.array(z.string()),
  error_code: z.string().nullable(),
  message: z.string().nullable(),
  ok: z.boolean(),
  total: z.number(),
  page: z.number(),
  size: z.number(),
  total_pages: z.number(),
});

export interface QueryFilters extends z.infer<typeof QueryFiltersSchema> {}

// Base schema for common query fields
const BaseQuerySchema = z.object({
  query_type: z.string(),
  query_hash: z.string(),
  execution_time: z.string(),
  warehouse_name: z.string(),
  user_name: z.string(),
  warehouse_size: z.string(),
  percentage_scanned_from_cache: z.number(),
  query_load_percent: z.string().optional(),
  instance_id: z.number(),
});

export const QueryItemSchema = BaseQuerySchema.extend({
  id: z.string(),
  query_id: z.string(),
  query_parameterized_hash: z.string(),
  bytes_scanned: z.string(),
  session_id: z.number(),
  query_text: z.string().nullable(),
  query_trimmed: z.string(),
  query_name: z.string().nullable(),
  query_cost: z.number(),
  insights: z.array(z.string()),
  start_time: z.string(),
  end_time: z.string(),
  partitions_scanned: z.string(),
  partitions_total: z.string(),
  bytes_spilled_to_local_storage: z.string(),
  bytes_spilled_to_remote_storage: z.string(),
  bytes_sent_over_network: z.string().nullable(),
  queued_overload_time: z.string(),
  queued_provisioning_time: z.string().nullable(),
  queued_repair_time: z.string().nullable(),
  data_store_id: z.string().nullable(),
  datasets: z.string().nullable(),
});

export interface QueryItem extends z.infer<typeof QueryItemSchema> {}

export const LastFetchedTimeResponseSchema = z.object({
  time_string: z.string(),
});

export interface LastFetchedTimeResponse extends z.infer<typeof LastFetchedTimeResponseSchema> {}

export interface SortOrder {
  query_cost: string;
  end_time: string;
  execution_time: string;
}

export type SortKey = keyof SortOrder;

export interface FilterParams {
  page: number;
  size: number;
  query_hash?: string;
  query_parameterized_hash?: string;
  query_id?: string;
  warehouses?: string[];
  tags?: string[];
  users?: string[];
  instance_ids?: string[];
  query_type?: string[];
  teams?: string[];
  sortAttribute?: string;
  sortOrder?: string;
  executionTimeFilter?: string;
  executionTimeFilterMode?: string;
  executionCostFilter?: string;
  executionCostFilterMode?: string;
  session_id?: string;
  start_date: string;
  end_date: string;
}

export const QueriesResponseSchema = z.object({
  items: z.array(QueryItemSchema),
  total: z.number(),
  pages: z.number(),
  page: z.number().optional(),
  size: z.number().optional(),
});

export interface QueriesResponse extends z.infer<typeof QueriesResponseSchema> {}

export const getSnowflakeJobItemSchema = z.object({
  apps: z.array(z.string()),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  last_runtime: z.number(),
  max_runtime: z.number(),
  min_runtime: z.number(),
  query_cost: z.number(),
  runs: z.number(),
  warehouse_names: z.array(z.string()),
  teams: z.array(z.string()),
  workflow_id: z.string(),
  dbt_project_name: z.string().optional().nullable(),
  tableau_dashboard_name: z.string().optional().nullable(),
  integration_environment: z.string().optional().nullable(),
  integration_name: z.string().optional().nullable(),
  test_coverage: z.number().nullable().optional(),
  instance_id: z.number().optional().nullable(),
});

export const getSnowflakeJobSummarySchema = z.object({
  integration_id: z.string().optional().nullable(),
  integration_environment_id: z.string().optional(),
  integration_environment_name: z.string().optional().nullable(),
  integration_name: z.string().optional().nullable(),
  workflow_id: z.string(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.coerce.number(),
  percentile_90_runtime: z.coerce.number(),
  last_runtime: z.string(),
  max_runtime: z.coerce.number(),
  min_runtime: z.coerce.number(),
  runs: z.number(),
  success_runs: z.number(),
  warehouse_names: z.array(z.string()),
  teams: z.array(z.string()),
  apps: z.array(z.string()),
  tableau_dashboard_name: z.string().optional().nullable(),
});

export const runAnnotationSchema = z.object({
  warehouse_change: z.string().nullable(),
  code_change: z.string().nullable(),
  execution_status: z.string().nullable(),
});

export const getSnowflakeJobRunItemSchema = z.object({
  run_id: z.string(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  success_count: z.number(),
  total_count: z.number(),
  timestamp: z.string(),
  start_time: z.string(),
  warehouses: z.array(z.string()),
  execution_statuses: z.array(z.string()),
  query_parameterized_hashes: z.array(z.string()),
  annotation: z.nullable(runAnnotationSchema),
  command_display: z.string().optional().nullable(),
});

export const getSnowflakeJobRunsGraphDataSchema = z.object({
  run_id: z.string(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  timestamp: z.string(),
  annotation: z.nullable(runAnnotationSchema),
});

export const dbtJobRunItem = z.object({
  run_id: z.string(),
  query_id: z.string().nullable(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  timestamp: z.string(),
  start_time: z.string(),
  execution_status: z.string(),
  error_message: z.string().nullable(),
  warehouse_name: z.string(),
  query_parameterized_hash: z.string(),
  insights: z.array(z.string()),
  annotation: z.nullable(runAnnotationSchema),
});

export const dbtModelRunSummary = z.object({
  total_cost: z.number(),
  avg_cost: z.number(),
  avg_execution_time: z.number(),
  max_execution_time: z.number(),
  run_count: z.number(),
  total_bytes_scanned: z.number(),
  avg_bytes_scanned: z.number(),
});

// -- dbt Job Listing schemas (Jobs tab) --
export const dbtJobListingItemSchema = z.object({
  command_display: z.string(),
  command_which: z.string(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  run_count: z.number(),
  success_count: z.number(),
  last_runtime: z.string(),
  warehouses: z.array(z.string()),
  dbt_cloud_job_id: z.string().nullable(),
});

export const dbtJobListingGraphItemSchema = z.object({
  command_display: z.string(),
  query_cost: z.number(),
  run_count: z.number(),
});

export const dbtJobListingSummarySchema = z.object({
  total_jobs: z.number(),
  total_cost: z.number(),
  total_runs: z.number(),
});

export type DbtJobListingItem = z.infer<typeof dbtJobListingItemSchema>;
export type DbtJobListingGraphItem = z.infer<typeof dbtJobListingGraphItemSchema>;
export type DbtJobListingSummary = z.infer<typeof dbtJobListingSummarySchema>;

export interface GetDbtJobListingParams {
  start_date?: string;
  end_date?: string;
  command?: string | string[];
  page?: number;
  size?: number;
  sortAttribute?: string;
  sortOrder?: string;
}

export interface GetDbtJobListingGraphParams {
  start_date?: string;
  end_date?: string;
  command?: string | string[];
}

export interface GetDbtJobListingSummaryParams {
  start_date?: string;
  end_date?: string;
  command?: string | string[];
}

export const dbtTasksSummarySchema = z.object({
  total_cost: z.number(),
  model_cost: z.number(),
  test_cost: z.number(),
  seed_cost: z.number(),
  snapshot_cost: z.number(),
  test_coverage: z.number().nullable(),
});

// -- dbt filter endpoint schemas --
export const dbtJobFilterItemSchema = z.object({
  command_display: z.string(),
});

export const dbtTagFilterItemSchema = z.object({
  tag: z.string(),
});

export type DbtJobFilterItem = z.infer<typeof dbtJobFilterItemSchema>;
export type DbtTagFilterItem = z.infer<typeof dbtTagFilterItemSchema>;

export const dbtModelSchema = z.object({
  task_id: z.string(),
  task_name: z.string(),
  task_type: z.string().optional().nullable(),
  integration_name: z.string().optional().nullable(),
  integration_environment: z.string().optional().nullable(),
  integration_id: z.string().nullable(),
  dbt_model_present: z.boolean(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  timestamp: z.string(),
  start_time: z.string(),
  success_count: z.number(),
  total_count: z.number(),
  warehouses: z.array(z.string()),
  annotation: z.nullable(runAnnotationSchema),
  query_parameterized_hashes: z.array(z.string()),
  error_messages: z.array(z.string()),
  execution_statuses: z.array(z.string()),
  insights: z.array(z.string()).default([]),
  commands: z.array(z.string()).optional().nullable(),
  tags: z.array(z.string()).optional().nullable(),
});

export const dbtProjectAndIntegrationSchema = z.object({
  dbt_project_name: z.string().optional().nullable(),
  integration_environment: z.string().optional().nullable(),
  integration_id: z.string().optional().nullable(),
});

export const getSnowflakeJobRunGraphDataSchema = z
  .object({
    run_id: z.string(),
    timestamp: z.string(),
  })
  .catchall(z.number());

export const getSnowflakeJobGraphDataSchema = z
  .object({
    date: z.string(),
  })
  .catchall(
    z.object({
      cost_value: z.number(),
      integration_name: z.string().nullable(),
      integration_environment_name: z.string().nullable(),
      dbt_project_name: z.string().nullable(),
    })
  );

export const getSnowflakeJobTaskItemSchema = z.object({
  task_id: z.string(),
  query_id: z.string(),
  hash: z.string(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  warehouse_name: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  execution_status: z.string(),
  error_message: z.string().optional(),
  optimal_warehouse_size: z.string().nullable().optional(),
});

export const snowflakeJobSettingsSchema = z.object({
  prefix: z.string().nullable(),
  workflow_id: z.string(),
  run_id: z.string().nullable().optional(),
  task_id: z.string().nullable().optional(),
  project_id: z.string(),
  app_id: z.string(),
  custom_tags: z.array(z.string()).max(15).nullable().optional(),
});

export const workloadAttributeSchema = z.object({
  key: z.string(),
  label: z.string(),
  is_custom: z.boolean(),
});

export const workloadAttributesSchema = z.object({
  options: z.array(workloadAttributeSchema),
});

export const workloadAttributeValuesSchema = z.object({
  attribute_key: z.string(),
  values: z.array(z.string()),
  total: z.number(),
  page: z.number(),
  size: z.number(),
});

export type WorkloadAttribute = z.infer<typeof workloadAttributeSchema>;
export type WorkloadAttributes = z.infer<typeof workloadAttributesSchema>;
export type WorkloadAttributeValues = z.infer<typeof workloadAttributeValuesSchema>;

const integrationWithEnvironmentsSchema = z.object({
  name: z.string(),
  environments: z.array(z.string()),
});

export type IntegrationWithEnvironments = z.infer<typeof integrationWithEnvironmentsSchema>;

// Bulk filter endpoint — only the bounded facets, populated per workflow_type.
// Unbounded facets (workflow_ids[_with_labels], custom_tags values) live behind
// per-facet paginated endpoints to keep this payload small (~100 KB worst case).
//
// Field availability:
// - warehouses: always
// - custom_tag_keys: CUSTOM only (capped at 15)
// - teams: TABLEAU only (always 1 element observed; CUSTOM uses paginated /teams)
// - apps: TABLEAU only (≤13 observed; CUSTOM uses paginated /apps)
// - integration_*: DBT only
// Fields not relevant to the requested workflow_type return [].
export const snowflakeJobFiltersSchema = z.object({
  warehouses: z.array(z.string()).optional().default([]),
  custom_tag_keys: z.array(z.string()).optional().default([]),
  teams: z.array(z.string()).optional().default([]),
  apps: z.array(z.string()).optional().default([]),
  integration_names: z.array(z.string()).optional().default([]),
  integration_environments: z.array(z.string()).optional().default([]),
  integrations_with_environments: z.array(integrationWithEnvironmentsSchema).optional().default([]),
});

// Per-facet paginated filter response (workflow_ids, teams, apps,
// custom_tags/{tag_key} return string values; workflow_ids_with_labels for
// TABLEAU returns label/value pairs).
const paginatedFilterSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    values: z.array(item),
    total: z.number(),
    page: z.number(),
    size: z.number(),
    total_pages: z.number(),
  });

export const snowflakeJobsFilterStringPageSchema = paginatedFilterSchema(z.string());
export const snowflakeJobsFilterLabelValuePageSchema = paginatedFilterSchema(
  z.object({ label: z.string(), value: z.string() })
);

export type SnowflakeJobsFilterStringPage = z.infer<typeof snowflakeJobsFilterStringPageSchema>;
export type SnowflakeJobsFilterLabelValuePage = z.infer<
  typeof snowflakeJobsFilterLabelValuePageSchema
>;

export interface SnowflakeJobsFilterPaginatedParams {
  start_date?: string;
  end_date?: string;
  workflow_type?: string;
  search?: string;
  page?: number;
  size?: number;
}

const taskInfoSchema = z.object({
  execution_time: z.number().optional(),
  query_cost: z.number().optional(),
  execution_status: z.string().optional(),
  error_message: z.string().optional(),
  warehouse_name: z.string().optional(),
  warehouse_size: z.string().optional(),
  query_hash: z.string().optional(),
});

export const runCompareDiffSchema = z.object({
  type: z.string(),
  task_id: z.string(),
  run1: taskInfoSchema,
  run2: taskInfoSchema,
});

const snowflakeJobDefinitionSchema = z.object({
  task_id: z.string(),
  hash: z.string(),
  query_cost: z.number(),
  execution_time: z.number(),
  run_count: z.number(),
  success_count: z.number(),
  insights: z.array(z.string()).default([]),
});

export const snowflakeJobDefinitionResponseSchema = z.object({
  run_id: z.string(),
  timestamp: z.string(),
  definition: z.array(snowflakeJobDefinitionSchema),
});

// Inferring Types
export type GetSnowflakeJobItem = z.infer<typeof getSnowflakeJobItemSchema>;
export type GetSnowflakeJobSummary = z.infer<typeof getSnowflakeJobSummarySchema>;
export type GetSnowflakeJobRunItem = z.infer<typeof getSnowflakeJobRunItemSchema>;
export type GetSnowflakeJobRunsGraphData = z.infer<typeof getSnowflakeJobRunsGraphDataSchema>;
export type DbtModelRunSummary = z.infer<typeof dbtModelRunSummary>;
export type DbtTasksSummary = z.infer<typeof dbtTasksSummarySchema>;
export type DbtModel = z.infer<typeof dbtModelSchema>;
export type DbtProjectAndIntegration = z.infer<typeof dbtProjectAndIntegrationSchema>;
export type DbtJobRunItem = z.infer<typeof dbtJobRunItem>;
export type GetSnowflakeJobRunGraphData = z.infer<typeof getSnowflakeJobRunGraphDataSchema>;
export type GetSnowflakeJobGraphData = z.infer<typeof getSnowflakeJobGraphDataSchema>;
export type GetSnowflakeJobTaskItem = z.infer<typeof getSnowflakeJobTaskItemSchema>;
export type SnowflakeJobSettings = z.infer<typeof snowflakeJobSettingsSchema>;
export type SnowflakeJobFilters = z.infer<typeof snowflakeJobFiltersSchema>;
export type RunCompareDiff = z.infer<typeof runCompareDiffSchema>;
export type SnowflakeJobDefinition = z.infer<typeof snowflakeJobDefinitionSchema>;
export type SnowflakeJobDefinitionResponse = z.infer<typeof snowflakeJobDefinitionResponseSchema>;
export type GetSnowflakeJobGraphDataRequestParams = {
  start_date: string;
  end_date?: string;
  workflows?: string[];
  warehouses?: string[];
  teams?: string[];
  apps?: string[];
  aggregate_by?: string;
  aggregate_date_interval?: string;
  workflow_type: WorkflowType;
  instance_id?: string[];
  custom_tag_filters?: Record<string, string[]>;
  integration_names?: string[];
  integration_environments?: string[];
};

export const QueryGroupsFiltersSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  sort_key: z.string().optional(),
  sort_order: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  tags: z.array(z.string()).optional(),
  users: z.array(z.string()).optional(),
  warehouses: z.array(z.string()).optional(),
  query_hash: z.string().optional(),
  instance_ids: z.array(z.string()).optional(),
});

export type QueryGroupsFilters = z.infer<typeof QueryGroupsFiltersSchema>;

export const QueryJobsFiltersSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  sort_key: z.string().optional(),
  sort_order: z.string().optional(),
  warehouses: z.array(z.string()).optional(),
  auto_tune: z.boolean().optional(),
  environments: z.array(z.string()).optional(),
  annualized_cost: z.string().optional(),
  job_id: z.string().optional(),
});

export type QueryJobsFilters = z.infer<typeof QueryJobsFiltersSchema>;

export const QueryJobExecutionsFiltersSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  sort_key: z.string().optional(),
  sort_order: z.string().optional(),
  executionCostFilter: z.string().optional(),
  warehouses: z.array(z.string()).optional(),
  warehouse_sizes: z.array(z.string()).optional(),
  users: z.array(z.string()).optional(),
});

export type QueryJobExecutionsFilters = z.infer<typeof QueryJobExecutionsFiltersSchema>;

export const QueryAllCSVFiltersSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  query_hash: z.string().optional(),
  query_parameterized_hash: z.string().optional(),
  query_id: z.string().optional(),
  warehouses: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  users: z.array(z.string()).optional(),
  sortAttribute: z.string().optional(),
  sortOrder: z.string().optional(),
  executionTimeFilter: z.string().optional(),
  executionTimeFilterMode: z.string().optional(),
  queryCostFilter: z.string().optional(),
  queryCostFilterMode: z.string().optional(),
  session_id: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});

export type QueryAllCSVFilters = z.infer<typeof QueryAllCSVFiltersSchema>;

export enum WorkflowType {
  DBT = "dbt",
  CUSTOM = "custom",
  TABLEAU = "tableau",
}

export enum WorkloadType {
  STREAMLIT = "STREAMLIT",
  NOTEBOOK = "NOTEBOOK",
  STORED_PROCEDURE = "CALL",
}

export const CommonGraphDataSchema = z.object({
  date: z.string(),
  cost: z.number(),
});

export const CommonGraphSummarySchema = z.object({
  total_cost: z.number(),
});

export const CommonJobListSchema = z.object({
  hash: z.string(),
  query_summary: z.string(),
  job_count: z.number(),
  cost: z.number(),
  query_type: z.nativeEnum(WorkloadType),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  warehouse_names: z.array(z.string()),
  database_names: z.array(z.string()).default([]),
});

export const CommonRunsSummarySchema = z.object({
  query_type: z.string(),
  total_cost: z.number(),
  avg_cost: z.number(),
  avg_execution_time: z.number(),
  max_execution_time: z.number(),
  min_execution_time: z.number(),
  run_count: z.number(),
  success_count: z.number(),
  total_bytes_scanned: z.number(),
  avg_bytes_scanned: z.number(),
  last_run_time: z.number(),
  max_cost_savings: z.number(),
  max_time_savings: z.number(),
});

export const CommonRunsListGraphDataSchema = z.object({
  query_cost: z.number(),
  execution_time: z.number(),
  timestamp: z.string(),
});

export const CommonRunsListSchema = z.object({
  query_summary: z.string(),
  query_cost: z.number(),
  bytes_scanned: z.number(),
  execution_time: z.number(),
  execution_status: z.string(),
  start_time: z.string(),
  timestamp: z.string(),
  database_name: z.string().nullable().optional(),
});

export const StreamlitNotebookRunsListSchema = CommonRunsListSchema.extend({
  session_id: z.number(),
});

export const StoredProceduresRunsListSchema = CommonRunsListSchema.extend({
  query_id: z.string(),
  hash: z.string(),
  database_name: z.string().nullable().optional(),
});

export const CommonChildQueriesSchema = z.object({
  query_type: z.nativeEnum(WorkloadType),
  query_tag: z.string(),
  rk: z.string(),
  query_summary: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  warehouse_name: z.string(),
  execution_status: z.string(),
  execution_time: z.number(),
  cost: z.number(),
  bytes_scanned: z.number(),
});

export const StreamlitNotebookChildQueriesSchema = CommonChildQueriesSchema.extend({
  session_id: z.string(),
});

export const StoredProceduresChildQueriesSchema = CommonChildQueriesSchema.extend({
  query_id: z.string(),
  has_child_query: z.boolean(),
  hash: z.string(),
});

export const StreamlitNotebookJobsGraphDataResponseSchema = z.object({
  data: z.array(CommonGraphDataSchema),
});

export const StoredProceduresJobsGraphDataSchema = z.object({
  data: z.array(CommonGraphDataSchema),
});

export const StoredProceduresJobsListFilterValuesSchema = z.object({
  warehouses: z.array(z.string()),
  databases: z.array(z.string()).default([]),
});

export type StreamlitNotebookJobsGraphData = z.infer<typeof CommonGraphDataSchema>;
export type StreamlitNotebookJobListResponse = z.infer<typeof CommonJobListSchema>;
export type StreamlitNotebookJobRunsSummaryDataResponse = z.infer<typeof CommonRunsSummarySchema>;
export type StreamlitNotebookJobsGraphDataResponse = z.infer<
  typeof StreamlitNotebookJobsGraphDataResponseSchema
>;

// DBT Health Check Schemas
export const dbtHealthCheckProjectInsightMetadataSchema = z
  .object({
    coverage: z.number().optional(),
    min_coverage_percent: z.number().optional(),
  })
  .passthrough();

export const dbtHealthCheckProjectInsightSchema = z.object({
  rk: z.string(),
  batch_id: z.string(),
  health_check_rk: z.string(),
  project_name: z.string(),
  package_name: z.string(),
  severity: z.string(),
  name: z.string(),
  type: z.string(),
  message: z.string(),
  recommendation: z.string(),
  reason_to_flag: z.string().nullable(),
  insight_metadata: dbtHealthCheckProjectInsightMetadataSchema,
});

export const dbtHealthCheckResultSchema = z.object({
  rk: z.string(),
  run_id: z.string(),
  integration_id: z.string().nullable(),
  integration_environment_id: z.string(),
  run_timestamp: z.string(),
  dbt_project_name: z.string(),
  dbt_version: z.string(),
  adapter_type: z.string(),
  manifest_generated_at: z.string(),
  manifest_invocation_id: z.string(),
  artifacts_present: z.record(z.boolean()),
  config_hash: z.string(),
  total_model_insights: z.number(),
  total_project_insights: z.number(),
  model_insights: z.array(z.any()), // Simplified for now
  project_insights: z.array(dbtHealthCheckProjectInsightSchema),
});

export const dbtHealthCheckResponseSchema = z.object({
  results: z.array(dbtHealthCheckResultSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

export type DbtHealthCheckProjectInsight = z.infer<typeof dbtHealthCheckProjectInsightSchema>;
export type DbtHealthCheckResult = z.infer<typeof dbtHealthCheckResultSchema>;
export type DbtHealthCheckResponse = z.infer<typeof dbtHealthCheckResponseSchema>;
export type StreamlitNotebookJobRunsListResponse = z.infer<typeof StreamlitNotebookRunsListSchema>;
export type StreamlitNotebookChildQueriesListResponse = z.infer<
  typeof StreamlitNotebookChildQueriesSchema
>;

export type StoredProceduresJobsGraphData = z.infer<typeof CommonGraphDataSchema>;
export type StoredProceduresJobsGraphDataResponse = z.infer<
  typeof StoredProceduresJobsGraphDataSchema
>;
export type StoredProceduresJobsListFilterValuesResponse = z.infer<
  typeof StoredProceduresJobsListFilterValuesSchema
>;
export type StoredProceduresJobListResponse = z.infer<typeof CommonJobListSchema>;
export type StoredProceduresJobRunsSummaryDataResponse = z.infer<typeof CommonRunsSummarySchema>;
export type StoredProceduresJobRunsGraphData = z.infer<typeof CommonRunsListGraphDataSchema>;
export type StoredProceduresJobRunsListResponse = z.infer<typeof StoredProceduresRunsListSchema>;
export type StoredProceduresChildQueriesListResponse = z.infer<
  typeof StoredProceduresChildQueriesSchema
>;

export type GetStoredProceduresJobsParams = {
  page?: number;
  size?: number;
  sort?: string;
  sortOrder?: string;
  start_date: string;
  end_date?: string;
  warehouses?: string[];
  databases?: string[];
};

export type GetStoredProceduresJobsGraphDataParams = {
  start_date: string;
  end_date?: string;
};

export type GetStoredProceduresJobsListFilterValuesParams = {
  start_date: string;
  end_date?: string;
};

export type GetStoredProceduresRunsSummaryParams = {
  start_date: string;
  end_date?: string;
};

export type GetStoredProceduresRunsGraphDataParams = {
  start_date: string;
  end_date?: string;
};

export type GetStoredProcedureRunsListParams = {
  start_date: string;
  end_date?: string;
  page?: number;
  size?: number;
};

export type GetStoredProceduresChildQueriesParams = {
  start_date: string;
  end_date?: string;
  page?: number;
  size?: number;
};

// Tasks (Snowflake tasks workload) Schemas
export const TaskListSchema = z.object({
  database_name: z.string(),
  schema_name: z.string(),
  name: z.string(),
  warehouse_name: z.string(),
  run_count: z.number(),
  success_count: z.number(),
  failure_count: z.number(),
  execution_time_ms: z.number(),
  queued_time_ms: z.number(),
  cost: z.number(),
  compute_cost: z.number(),
  serverless_cost: z.number(),
  credits_used: z.number(),
  serverless_credits_used: z.number(),
});

export const TasksGraphDataSchema = z.object({
  data: z.array(CommonGraphDataSchema),
});

export const TasksListFilterValuesSchema = z.object({
  warehouses: z.array(z.string()).default([]),
  databases: z.array(z.string()).default([]),
  schemas: z.array(z.string()).default([]),
});

export type TaskListResponse = z.infer<typeof TaskListSchema>;
export type TasksGraphData = z.infer<typeof CommonGraphDataSchema>;
export type TasksGraphDataResponse = z.infer<typeof TasksGraphDataSchema>;
export type TasksListFilterValuesResponse = z.infer<typeof TasksListFilterValuesSchema>;

export type GetTasksJobsParams = {
  page?: number;
  size?: number;
  sort?: string;
  sortOrder?: string;
  start_date: string;
  end_date?: string;
  warehouses?: string[];
  databases?: string[];
  schemas?: string[];
};

export type GetTasksDateParams = {
  start_date: string;
  end_date?: string;
};

// One Snowflake task run from GET /query/v2/tasks/runs (the name-click drill-down)
export const TaskRunSchema = z.object({
  run_id: z.number(),
  attempt_number: z.number(),
  name: z.string(),
  database_name: z.string(),
  schema_name: z.string(),
  query_id: z.string(),

  task_database_id: z.number().nullable(),
  task_schema_id: z.number().nullable(),

  root_task_id: z.string().nullable(),
  graph_version: z.number().nullable(),
  graph_run_group_id: z.string().nullable(),

  state: z.string(),
  scheduled_from: z.string().nullable(),
  scheduled_time: z.string(),
  query_start_time: z.string().nullable(),
  completed_time: z.string().nullable(),

  queued_time_ms: z.number().nullable(),
  execution_time_ms: z.number().nullable(),

  error_code: z.string().nullable(),
  error_message: z.string().nullable(),
  return_value: z.string().nullable(),

  condition_text: z.string().nullable(),
  config: z.string().nullable(),
  backfill_info: z.string().nullable(),
});

export type TaskRunResponse = z.infer<typeof TaskRunSchema>;

// Canonical Snowflake task run states.
// https://docs.snowflake.com/en/sql-reference/functions/task_history (state column)
// Order matches the lifecycle a single run can traverse, ending with the
// "no SQL was executed" terminal state SKIPPED.
export const TASK_RUN_STATES = [
  "SCHEDULED",
  "EXECUTING",
  "SUCCEEDED",
  "FAILED",
  "FAILED_AND_AUTO_SUSPENDED",
  "CANCELLED",
  "SKIPPED",
] as const;

export type TaskRunState = (typeof TASK_RUN_STATES)[number];

export type GetTasksRunsParams = {
  database_name: string;
  schema_name: string;
  name: string;
  page?: number;
  size?: number;
  sortAttribute?: string;
  sortOrder?: string;
  /** Multi-select filter for run state. Sent as repeated `states=<value>`
   *  query params (e.g. `?states=SUCCEEDED&states=FAILED`). Omit / empty
   *  array = no state filter (return all states). */
  states?: string[];
};

// GET /query/v2/tasks/runs_summary — aggregate stats across one task's run history.
// Identified by database_name + schema_name + name (no workload hash, unlike sprocs).
export const TaskRunsSummarySchema = z.object({
  database_name: z.string(),
  schema_name: z.string(),
  name: z.string(),
  total_cost: z.number(),
  avg_cost: z.number(),
  run_count: z.number(),
  success_count: z.number(),
  failure_count: z.number(),
  execution_time_ms: z.number(),
  queued_time_ms: z.number(),
  avg_execution_time_ms: z.number(),
  avg_queued_time_ms: z.number(),
  compute_cost: z.number(),
  serverless_cost: z.number(),
  credits_used: z.number(),
  serverless_credits_used: z.number(),
  last_run_date: z.string().nullable(),
});
export type TaskRunsSummaryResponse = z.infer<typeof TaskRunsSummarySchema>;

// GET /query/v2/tasks/runs_graph_data — per-day cost + execution time for one task.
// Wrapped in `data` (unlike the sproc runs_graph_data which returns a bare array).
export const TaskRunsGraphDataPointSchema = z.object({
  date: z.string(),
  cost: z.number(),
  execution_time_ms: z.number(),
});
export const TaskRunsGraphDataSchema = z.object({
  data: z.array(TaskRunsGraphDataPointSchema),
});
export type TaskRunsGraphDataPoint = z.infer<typeof TaskRunsGraphDataPointSchema>;
export type TaskRunsGraphDataResponse = z.infer<typeof TaskRunsGraphDataSchema>;

export interface GetSnowflakeJobsParams {
  page?: number;
  size?: number;
  start_date?: string;
  end_date?: string;
  workflow_type?: string;
  workflows?: string[];
  warehouses?: string[];
  teams?: string[];
  apps?: string[];
  instance_id?: string[];
  sortAttribute?: string;
  sortOrder?: string;
  executionTimeFilter?: number;
  executionTimeFilterMode?: string;
  costFilter?: string;
  costFilterMode?: string;
  custom_tag_filters?: Record<string, string[]>;
  integration_names?: string[];
  integration_environments?: string[];
}

export interface GetSnowflakeJobRunsParams {
  start_date?: string;
  end_date?: string;
  task_id?: string;
  instance_id?: string;
  page?: number;
  size?: number;
  sort_key?: string;
  sort_order?: string;
  run_status?: string;
  command?: string | string[];
}

export interface GetSnowflakeJobRunsGraphDataParams {
  start_date?: string;
  end_date?: string;
  task_id?: string;
  instance_id?: string;
  sort_key?: string;
  sort_order?: string;
}

export interface GetSnowflakeJobRunTasksParams {
  start_date?: string;
  end_date?: string;
  page?: number;
  size?: number;
  run_id?: string;
  task_type?: string[];
  sort_key?: string;
  sort_order?: string;
}

export interface GetSnowflakeJobFiltersParams {
  start_date?: string;
  end_date?: string;
  workflow_type?: string;
}

export interface GetSnowflakeJobDefinitionParams {
  start_date?: string;
  end_date?: string;
}

export enum FilterEntityType {
  USERS = "users",
  WAREHOUSES = "warehouses",
  TAGS = "tags",
  ROLES = "roles",
  QUERY_TYPES = "query_types",
}

export interface GetQueryFiltersParams {
  entity_type?: FilterEntityType | string;
  prefix?: string;
  page?: number;
  size?: number;
  filter_type?: string;
}

// Query Details Schemas
export const QueryDetailsItemSchema = BaseQuerySchema.extend({
  query_text: z.string(),
  cost: z.union([z.string(), z.number()]),
  percent_partition_scanned: z.string().optional(),
  bytes_spilled: z.string().optional(),
  timestamp: z.string(),
  query_hash_count: z.number().optional().nullable(),
  tags: z.array(z.unknown()).optional(),
})
  .omit({
    percentage_scanned_from_cache: true,
    instance_id: true,
  })
  .extend({
    percentage_scanned_from_cache: z.coerce.string().optional(),
    instance_id: z.coerce.string().optional(),
  });

export interface QueryDetailsItem extends z.infer<typeof QueryDetailsItemSchema> {}

export const QueryDetailsResponseSchema = z.object({
  query: QueryDetailsItemSchema,
});

export interface QueryDetailsResponse extends z.infer<typeof QueryDetailsResponseSchema> {}

// Query Jobs API Types
export const QueryJobItemSchema = z.object({
  job_id: z.string(),
  job_name: z.string().nullable().optional(),
  number_of_runs: z.number().nullable().optional(),
  annualized_cost: z.string().nullable().optional(),
  number_of_steps: z.number().nullable().optional(),
  warehouses: z.array(z.string()).nullable().optional(),
  insights: z.array(z.string()).nullable().optional(),
  avg_total_execution_time: z.string().nullable().optional(),
  median_total_execution_time: z.string().nullable().optional(),
  max_total_execution_time: z.string().nullable().optional(),
  p90_total_execution_time: z.string().nullable().optional(),
  job_frequency: z.string().nullable().optional(),
  environment: z.string().nullable().optional(),
  avg_local_spill: z.string().nullable().optional(),
  avg_remote_spill: z.string().nullable().optional(),
});

export interface QueryJobItem extends z.infer<typeof QueryJobItemSchema> {}

export const QueryJobsResponseSchema = pageSchema(QueryJobItemSchema);
export type QueryJobsResponse = z.infer<typeof QueryJobsResponseSchema>;

// Update Query Job Name Types
export const UpdateQueryJobNameRequestSchema = z.object({
  job_name: z.string(),
});

export interface UpdateQueryJobNameRequest extends z.infer<
  typeof UpdateQueryJobNameRequestSchema
> {}

export const UpdateQueryJobNameResponseSchema = StandardResponseSchema;
export type UpdateQueryJobNameResponse = z.infer<typeof UpdateQueryJobNameResponseSchema>;

// Update Query Group Name Types
export const UpdateQueryGroupNameRequestSchema = z.object({
  group_name: z.string(),
});

export interface UpdateQueryGroupNameRequest extends z.infer<
  typeof UpdateQueryGroupNameRequestSchema
> {}

export const UpdateQueryGroupNameResponseSchema = StandardResponseSchema;
export type UpdateQueryGroupNameResponse = z.infer<typeof UpdateQueryGroupNameResponseSchema>;

// Query Group Stats (for hover details)
export const QueryGroupStatsSchema = z.object({
  num_runs: z.number(),
  total_cost: z.string(),
  avg_execution_time: z.string(),
  last_run_time: z.string(),
  max_cost_savings: z.string(),
  max_time_savings: z.string(),
});

export type QueryGroupStats = z.infer<typeof QueryGroupStatsSchema>;

// Period enum for graph data endpoints
export enum Period {
  YEAR = 365,
  MONTH_29 = 29,
  MONTH = 30,
  WEEK = 7,
  DAY = 1,
  FOUR_WEEKS = 28,
}

export interface GetQueryJobFiltersParams {
  job_id?: string;
  filter_type?: string;
  auto_tune?: boolean;
}

export interface JobFilters {
  costFilter?: string | string[] | number;
  warehouseFilter?: string[];
  userFilter?: string[];
  warehouseSizeFilter?: string[];
  environmentFilter?: string | string[];
}

export interface GetQueryJobDataParams {
  period?: Period;
}

export interface GetQueryGroupDataParams {
  period?: Period;
}

// Query Job Filters Response Schema
export const QueryJobFiltersResponseSchema = z.object({
  warehouses: z.array(z.string().nullable()).nullable().optional(),
  users: z.array(z.string().nullable()).nullable().optional(),
  environments: z.array(z.string().nullable()).nullable().optional(),
  warehouse_sizes: z.array(z.string().nullable()).nullable().optional(),
  ok: z.boolean(),
  error_code: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});

export type QueryJobFiltersResponse = z.infer<typeof QueryJobFiltersResponseSchema>;

// Query Graph Data Response Schema (for job and group graph data)
export const QueryGraphDataItemSchema = z.object({
  date: z.string(),
  cost: z.number(),
  execution_time: z.number(),
});

export const QueryGraphDataResponseSchema = z.object({
  data: z.array(QueryGraphDataItemSchema),
});

export type QueryGraphDataItem = z.infer<typeof QueryGraphDataItemSchema>;
export type QueryGraphDataResponse = z.infer<typeof QueryGraphDataResponseSchema>;

// Query Job Execution Response Schema
export const QueryJobExecutionItemSchema = z.object({
  job_id: z.string(),
  session_id: z.number(),
  start_time: z.string(),
  end_time: z.string(),
  insights: z.array(z.string()).nullable().optional(),
  warehouse_name: z.string(),
  warehouse_size: z.string().nullable().optional(),
  user_name: z.string(),
  job_execution_time: z.string(),
  cost: z.string(),
  total_data_scanned: z.string(),
  total_data_sent_over_network: z.string(),
  total_local_spill: z.string(),
  total_remote_spill: z.string(),
});

export type QueryJobExecutionItem = z.infer<typeof QueryJobExecutionItemSchema>;
export const QueryJobExecutionsResponseSchema = pageSchema(QueryJobExecutionItemSchema);
export type QueryJobExecutionsResponse = z.infer<typeof QueryJobExecutionsResponseSchema>;

// Query Job Definition Response Schema
export const QueryJobDefinitionItemSchema = z.object({
  job_id: z.string(),
  step_id: z.string(),
  query_parameterized_hash: z.string(),
  query_text: z.string(),
  query_name: z.string(),
  query_trimmed: z.string(),
  avg_cost: z.string(),
  avg_execution_time: z.string(),
  p90_execution_time: z.string(),
  avg_data_scanned: z.string(),
  avg_data_sent_over_network: z.string(),
  avg_local_spill: z.string(),
  avg_remote_spill: z.string(),
});

export type QueryJobDefinitionItem = z.infer<typeof QueryJobDefinitionItemSchema>;
export const QueryJobDefinitionsResponseSchema = z.array(QueryJobDefinitionItemSchema);
export type QueryJobDefinitionsResponse = z.infer<typeof QueryJobDefinitionsResponseSchema>;

// DBT Job API Params
export interface GetDbtJobRunsListParams {
  startDate?: string;
  endDate?: string;
  task_id?: string | null;
  sort_key?: string | null;
  sort_order?: string;
  page?: number;
  size?: number;
  execution_status?: string[];
  warehouse_name?: string;
}

export interface GetDbtJobRunsListGraphDataParams {
  startDate?: string;
  endDate?: string;
  task_id?: string | null;
  sort_key?: string;
  sort_order?: string;
  execution_status?: string[];
  warehouse_name?: string;
}

export interface GetDbtModelRunSummaryParams {
  task_id?: string | null;
  startDate?: string;
  endDate?: string;
}

export interface GetDbtTasksSummaryParams {
  task_id?: string | null;
  start_date?: string;
  end_date?: string;
  task_type?: string[];
  run_id?: string | null;
  page?: number;
  size?: number;
}

export interface GetDbtModelsListParams {
  start_date?: string;
  end_date?: string;
  page?: number;
  size?: number;
  run_id?: string | null;
  task_type?: string[];
  sortAttribute?: string;
  sortOrder?: string;
  command?: string | string[];
}
export type CommonGraphSummaryResponse = z.infer<typeof CommonGraphSummarySchema>;
